import React, { useState, useEffect } from 'react';
import QuranService from '../../services/quranService';
import api from '../../api/api';

const EditVerseRangeModal = ({ assignment, onSave, onCancel }) => {
  const allSurahs = QuranService.getAllSurahs();

  // Initialize state from assignment
  const [startSurahId, setStartSurahId] = useState('');
  const [startVerse, setStartVerse] = useState('');
  const [endSurahId, setEndSurahId] = useState('');
  const [endVerse, setEndVerse] = useState('');
  const [recalculate, setRecalculate] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [subsequentCount, setSubsequentCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (assignment && assignment.range) {
      setStartSurahId(assignment.range.start.surah.id);
      setStartVerse(assignment.range.start.verse);
      setEndSurahId(assignment.range.end.surah.id);
      setEndVerse(assignment.range.end.verse);
    }

    // Fetch subsequent assignment count for recalculation preview
    if (assignment && assignment.planId && assignment._id) {
      fetchSubsequentAssignmentCount();
    }
  }, [assignment]);

  // Fetch count of subsequent pending assignments
  const fetchSubsequentAssignmentCount = async () => {
    try {
      if (!assignment.planId || !assignment._id) return;

      const response = await api.get(`/api/learning-plans/${assignment.planId}/assignments`);
      const allAssignments = response.data;

      // Count pending assignments after this one
      const subsequentPending = allAssignments.filter(a =>
        a._id !== assignment._id &&
        new Date(a.dueDate) > new Date(assignment.dueDate) &&
        a.status !== 'completed'
      );

      setSubsequentCount(subsequentPending.length);
    } catch (error) {
      console.error('Error fetching subsequent assignments:', error);
    }
  };

  // Check if recalculation should be disabled
  const isRecalculationDisabled = () => {
    // Disable if assignment is completed
    if (assignment.status === 'completed') return true;

    // Disable if assignment has no planId (manual assignment)
    if (!assignment.planId) return true;

    // Disable if no subsequent pending assignments
    if (subsequentCount === 0) return true;

    return false;
  };

  const getRecalculationDisabledReason = () => {
    if (assignment.status === 'completed') {
      return 'Cannot recalculate completed assignments';
    }
    if (!assignment.planId) {
      return 'Only plan-generated assignments support recalculation';
    }
    if (subsequentCount === 0) {
      return 'This is the last pending assignment. No recalculation needed.';
    }
    return '';
  };

  const handleSave = () => {
    // Validate inputs
    const validation = validateInputs();
    if (!validation.valid) {
      alert(validation.message);
      return;
    }

    // If recalculate is checked, show confirmation dialog
    if (recalculate && subsequentCount > 0) {
      setShowConfirmation(true);
    } else {
      // No recalculation, proceed with save
      performSave();
    }
  };

  const validateInputs = () => {
    const startSurah = allSurahs.find(s => s.id === startSurahId);
    const endSurah = allSurahs.find(s => s.id === endSurahId);

    if (!startSurah || !endSurah) {
      return { valid: false, message: 'Invalid surah selection' };
    }

    if (!startVerse || !endVerse) {
      return { valid: false, message: 'Please enter verse numbers' };
    }

    const startVerseNum = parseInt(startVerse);
    const endVerseNum = parseInt(endVerse);

    if (startVerseNum < 1 || startVerseNum > startSurah.verseCount) {
      return { valid: false, message: `Start verse must be between 1 and ${startSurah.verseCount} for ${startSurah.name}` };
    }

    if (endVerseNum < 1 || endVerseNum > endSurah.verseCount) {
      return { valid: false, message: `End verse must be between 1 and ${endSurah.verseCount} for ${endSurah.name}` };
    }

    // Validate range logic
    if (startSurahId === endSurahId && endVerseNum < startVerseNum) {
      return { valid: false, message: `End verse (${endVerseNum}) cannot be before start verse (${startVerseNum}) in the same surah` };
    }

    return { valid: true };
  };

  const performSave = async () => {
    const startSurah = allSurahs.find(s => s.id === startSurahId);
    const endSurah = allSurahs.find(s => s.id === endSurahId);
    const startVerseNum = parseInt(startVerse);
    const endVerseNum = parseInt(endVerse);

    // Create updated range
    const updatedRange = {
      start: {
        surah: { id: startSurahId, name: startSurah.name },
        verse: startVerseNum
      },
      end: {
        surah: { id: endSurahId, name: endSurah.name },
        verse: endVerseNum
      }
    };

    // Create updated title
    let updatedTitle;
    if (startSurahId === endSurahId) {
      updatedTitle = `${startSurah.name} ${startVerseNum}-${endVerseNum}`;
    } else {
      updatedTitle = `${startSurah.name} ${startVerseNum} to ${endSurah.name} ${endVerseNum}`;
    }

    // If recalculate is checked and assignment has _id, use the new endpoint
    if (recalculate && assignment._id && assignment.planId) {
      setLoading(true);
      try {
        const response = await api.put(`/api/assignments/${assignment._id}/edit-and-recalculate`, {
          updates: {
            range: updatedRange,
            title: updatedTitle
          },
          recalculate: true
        });

        // Pass the entire response to parent (includes regeneration summary)
        onSave({
          ...assignment,
          range: updatedRange,
          title: updatedTitle,
          _recalculationResult: response.data
        });
      } catch (error) {
        console.error('Error recalculating assignments:', error);
        alert(error.response?.data?.message || 'Failed to recalculate assignments. Please try again.');
        setLoading(false);
        return;
      }
    } else {
      // Standard save (no recalculation)
      onSave({
        ...assignment,
        range: updatedRange,
        title: updatedTitle
      });
    }
  };

  const handleConfirmRecalculation = () => {
    setShowConfirmation(false);
    performSave();
  };

  const handleCancelRecalculation = () => {
    setShowConfirmation(false);
  };

  // Get max verse for selected surah
  const getMaxVerse = (surahId) => {
    const surah = allSurahs.find(s => s.id === surahId);
    return surah ? surah.verseCount : 1;
  };

  // Calculate total verses in range
  const calculateVerseCount = () => {
    if (!startSurahId || !endSurahId || !startVerse || !endVerse) {
      return 0;
    }

    const startSurahNum = parseInt(startSurahId);
    const endSurahNum = parseInt(endSurahId);
    const startVerseNum = parseInt(startVerse);
    const endVerseNum = parseInt(endVerse);

    if (startSurahNum === endSurahNum) {
      // Same surah
      return Math.max(0, endVerseNum - startVerseNum + 1);
    }

    // Multiple surahs - approximate (for display only)
    const startSurah = allSurahs.find(s => s.id === startSurahId);
    const endSurah = allSurahs.find(s => s.id === endSurahId);

    if (!startSurah || !endSurah) return 0;

    // Start surah verses
    let total = startSurah.verseCount - startVerseNum + 1;

    // Middle surahs (if any)
    const direction = startSurahNum < endSurahNum ? 1 : -1;
    let currentSurah = startSurahNum + direction;
    while (currentSurah !== endSurahNum) {
      const surah = allSurahs.find(s => s.id === String(currentSurah));
      if (surah) total += surah.verseCount;
      currentSurah += direction;
    }

    // End surah verses
    total += endVerseNum;

    return total;
  };

  const verseCount = calculateVerseCount();

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onCancel}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ marginTop: 0, marginBottom: '8px', color: '#333' }}>
          Edit Verse Range
        </h2>

        {/* Warning for line-based assignments */}
        {assignment.metadata?.estimatedLines && (
          <div style={{
            marginBottom: '15px',
            padding: '10px 12px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px',
            fontSize: '13px',
            color: '#856404'
          }}>
            <strong>⚠️ Note:</strong> This is a line-based assignment. Editing the verse range will not recalculate line estimates. Original metadata will be preserved.
          </div>
        )}

        {/* Verse Count Display */}
        {verseCount > 0 && (
          <div style={{
            marginBottom: '20px',
            padding: '8px 12px',
            backgroundColor: '#f0f0f0',
            borderRadius: '4px',
            fontSize: '13px',
            color: '#666'
          }}>
            Total: <strong>{verseCount} verse{verseCount !== 1 ? 's' : ''}</strong>
          </div>
        )}

        {/* Start Range */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>From</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                Surah
              </label>
              <select
                value={startSurahId}
                onChange={(e) => setStartSurahId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '14px'
                }}
              >
                <option value="">Select surah...</option>
                {allSurahs.map((surah) => (
                  <option key={surah.id} value={surah.id}>
                    {surah.id}. {surah.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                Verse
              </label>
              <input
                type="number"
                min="1"
                max={getMaxVerse(startSurahId)}
                value={startVerse}
                onChange={(e) => setStartVerse(e.target.value)}
                placeholder="1"
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>
        </div>

        {/* End Range */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>To</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                Surah
              </label>
              <select
                value={endSurahId}
                onChange={(e) => setEndSurahId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '14px'
                }}
              >
                <option value="">Select surah...</option>
                {allSurahs.map((surah) => (
                  <option key={surah.id} value={surah.id}>
                    {surah.id}. {surah.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                Verse
              </label>
              <input
                type="number"
                min="1"
                max={getMaxVerse(endSurahId)}
                value={endVerse}
                onChange={(e) => setEndVerse(e.target.value)}
                placeholder="1"
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>
        </div>

        {/* Recalculation Checkbox (Phase 1.6) */}
        {assignment.planId && assignment._id && (
          <div style={{ marginBottom: '20px' }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                color: isRecalculationDisabled() ? '#999' : '#333',
                cursor: isRecalculationDisabled() ? 'not-allowed' : 'pointer',
                userSelect: 'none'
              }}
            >
              <input
                type="checkbox"
                checked={recalculate}
                onChange={(e) => setRecalculate(e.target.checked)}
                disabled={isRecalculationDisabled()}
                style={{
                  cursor: isRecalculationDisabled() ? 'not-allowed' : 'pointer'
                }}
              />
              <span>Recalculate all assignments after this one</span>
            </label>
            {recalculate && subsequentCount > 0 && (
              <div style={{
                marginTop: '8px',
                marginLeft: '24px',
                padding: '8px 12px',
                backgroundColor: '#e8f4fd',
                border: '1px solid #0066cc',
                borderRadius: '4px',
                fontSize: '13px',
                color: '#0066cc'
              }}>
                ℹ️ This will delete and regenerate {subsequentCount} pending assignment{subsequentCount !== 1 ? 's' : ''}
              </div>
            )}
            {isRecalculationDisabled() && (
              <div style={{
                marginTop: '8px',
                marginLeft: '24px',
                fontSize: '12px',
                color: '#999'
              }}>
                {getRecalculationDisabledReason()}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            style={{
              padding: '10px 20px',
              borderRadius: '4px',
              border: '1px solid #ddd',
              backgroundColor: 'white',
              color: '#666',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              opacity: loading ? 0.6 : 1
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={loading}
            style={{
              padding: '10px 20px',
              borderRadius: '4px',
              border: 'none',
              backgroundColor: '#6C5CE7',
              color: 'white',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Confirmation Dialog (Phase 1.6) */}
      {showConfirmation && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '450px',
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '16px', color: '#d9534f' }}>
              ⚠️ Confirm Recalculation
            </h3>

            <div style={{ marginBottom: '20px', fontSize: '14px', color: '#333', lineHeight: '1.6' }}>
              <p style={{ marginBottom: '12px' }}>This will:</p>
              <ul style={{ marginLeft: '20px', marginBottom: '12px' }}>
                <li>Delete {subsequentCount} pending assignment{subsequentCount !== 1 ? 's' : ''}</li>
                <li>Regenerate new assignments starting from where this assignment ends</li>
                <li>Preserve all completed assignments</li>
              </ul>
              <p style={{ marginBottom: 0, fontWeight: '500' }}>
                Completed work will NOT be affected.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleCancelRecalculation}
                style={{
                  padding: '10px 20px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  backgroundColor: 'white',
                  color: '#666',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRecalculation}
                style={{
                  padding: '10px 20px',
                  borderRadius: '4px',
                  border: 'none',
                  backgroundColor: '#d9534f',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Confirm Recalculation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditVerseRangeModal;
